import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  createPreset,
  deletePreset,
  launchPreset,
  listPresets,
  updatePreset,
} from './presetsAdminApi'
import {
  EvalRunPresetCreateInput,
  EvalRunPresetUpdateInput,
  PresetFilters,
  PresetLaunchOverrides,
} from './presetsAdminModel'

const ADMIN_LIST_KEY = ['presetsAdmin', 'list'] as const
// A launch creates a run; invalidate the runs list so it shows up there.
const RUNS_LIST_KEY = ['runsAdmin', 'list'] as const

export const usePresetsListQuery = (filters: PresetFilters) => {
  return useQuery({
    queryKey: [...ADMIN_LIST_KEY, filters],
    queryFn: () => listPresets(filters),
    keepPreviousData: true,
    retry: false,
    onError: () => {
      notification.error({ message: 'Failed to load presets' })
    },
  })
}

export const useCreatePresetMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (input: EvalRunPresetCreateInput) => createPreset(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ADMIN_LIST_KEY)
        notification.success({ message: 'Preset saved' })
      },
      onError: (err) => {
        const description = err instanceof Error ? err.message : undefined
        notification.error({ message: 'Failed to save preset', description })
      },
    },
  )
}

export const useUpdatePresetMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (input: EvalRunPresetUpdateInput) => updatePreset(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ADMIN_LIST_KEY)
        notification.success({ message: 'Preset updated' })
      },
      onError: (err) => {
        const description = err instanceof Error ? err.message : undefined
        notification.error({ message: 'Failed to update preset', description })
      },
    },
  )
}

export const useDeletePresetMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((presetId: string) => deletePreset(presetId), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Preset deleted' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to delete preset', description })
    },
  })
}

type LaunchArgs = { presetId: string; overrides: PresetLaunchOverrides }

export const useLaunchPresetMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ presetId, overrides }: LaunchArgs) => launchPreset(presetId, overrides),
    {
      onSuccess: (accepted) => {
        queryClient.invalidateQueries(RUNS_LIST_KEY)
        notification.success({
          message: 'Run started',
          description: `Queued ${accepted.planned_trials} trial(s).`,
        })
      },
      onError: (err) => {
        const description = err instanceof Error ? err.message : undefined
        notification.error({ message: 'Failed to start run', description })
      },
    },
  )
}
